import { useState } from "react";
import { ArrowUp, Sparkle } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { usePermissions } from "@/lib/permissions";
import { Bubble, ProposalCard, useAssistant } from "@/lib/assistantShared";

/**
 * Floating assistant launcher for record pages (customers, jobs).
 * context: { kind, label, prompts: string[] }. Office staff only.
 */
export function AssistantDock({ context }) {
  const { can, loaded } = usePermissions();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const { messages, busy, send, confirmProp, cancelProp } = useAssistant({ context });

  if (!loaded || !can("write:accounts")) return null;

  const prompts = context?.prompts || [];
  const label = context?.label || "";

  const submit = async (text) => {
    const q = (text ?? draft).trim();
    if (!q || busy) return;
    setDraft("");
    await send(q);
  };

  return (
    <>
      <button
        type="button"
        data-testid="assistant-dock-launcher"
        onClick={() => setOpen(true)}
        title="Ask the assistant"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-500 text-white shadow-lg shadow-indigo-500/30 transition-transform duration-150 hover:scale-105 hover:bg-indigo-400"
      >
        <Sparkle size={22} weight="bold" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          data-testid="assistant-dock-panel"
          className="flex w-full flex-col gap-0 border-zinc-800 bg-zinc-950 p-0 sm:max-w-md"
        >
          <div className="border-b border-zinc-800 p-4">
            <SheetTitle className="flex items-center gap-2 text-sm font-bold text-zinc-100">
              <Sparkle size={16} weight="bold" className="text-indigo-400" /> Assistant
              {label && <span className="text-xs font-normal text-zinc-500">· {label}</span>}
            </SheetTitle>
          </div>

          <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
            {messages.length === 0 && (
              <div className="text-sm text-zinc-500">
                <p>
                  Ask about this record, or tell me to make a change — I'll show the plan and wait
                  for your OK.
                </p>
                {prompts.length > 0 && (
                  <div className="mt-4 flex flex-col gap-2">
                    {prompts.map((p) => (
                      <button
                        key={p}
                        type="button"
                        data-testid="assistant-dock-prompt"
                        onClick={() => setDraft(p)}
                        className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-left text-xs text-zinc-400 transition-colors duration-150 hover:border-indigo-400/50 hover:text-zinc-100"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {messages.map((m) =>
              m.kind === "proposal" ? (
                <ProposalCard
                  key={m.key}
                  item={m}
                  onConfirm={confirmProp}
                  onCancel={cancelProp}
                />
              ) : (
                <Bubble key={m.key} role={m.role} text={m.text} />
              ),
            )}
            {busy && (
              <div
                className="self-start rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-500"
                data-testid="assistant-dock-thinking"
              >
                Thinking…
              </div>
            )}
          </div>

          <div className="flex items-end gap-2 border-t border-zinc-800 p-3">
            <textarea
              data-testid="assistant-dock-input"
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Ask or tell me to do something…"
              className="min-h-[48px] flex-1 resize-none rounded-lg border border-zinc-800 bg-zinc-900 p-2.5 text-sm text-zinc-200 outline-none focus:border-indigo-500"
            />
            <button
              type="button"
              data-testid="assistant-dock-send"
              disabled={busy || !draft.trim()}
              onClick={() => submit()}
              className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-500 text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40"
            >
              {busy ? (
                <Sparkle size={16} weight="bold" className="animate-pulse" />
              ) : (
                <ArrowUp size={17} weight="bold" />
              )}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
